import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import type { User } from '@supabase/supabase-js'
import {
  supabase,
  signInWithEmail,
  signUpWithEmail,
  signOut as supabaseSignOut,
  getSession,
  mfaGetAal,
  updateUserDisplayName,
} from '@/lib/supabase'
import { api, updateOrganizationUserName } from '@/lib/api'
import { identifyUser, resetUser, captureEvent } from '@/lib/posthog'
import { resetAllStores } from '@/stores/reset'
import type { Organization } from '@/lib/schemas'

interface UserProfile {
  id: string
  email: string
  fullName: string | null
  organizationId: string | null
  role: 'admin' | 'member' | 'viewer'
  isPlatformAdmin: boolean
  isOrgActive: boolean
  mfaEnrolledAt: string | null
  mfaRequired: boolean
  notificationPrefs?: {
    emailEnabled?: boolean
    types?: Partial<Record<string, boolean>>
  }
}

interface MfaStatusResponse {
  enrolled: boolean
  enrolledAt: string | null
  recoveryCodesRemaining: number
  required: boolean
}

interface AuthState {
  // State
  user: User | null
  organization: Organization | null
  role: 'admin' | 'member' | 'viewer'
  /**
   * False from the moment a session restore or sign-in starts until the
   * profile (and so `role`) has loaded. The shell renders before the profile
   * arrives, with `role` at its 'member' default, so anything that would open
   * an AI session — refused to a viewer — waits for this. True at rest (no
   * user, or role known); a profile that fails to load also ends the wait.
   */
  isRoleLoaded: boolean
  isPlatformAdmin: boolean
  isOrgActive: boolean
  aal: 'aal1' | 'aal2'
  mfaEnrolled: boolean
  mfaEnrolledAt: string | null
  mfaRequired: boolean
  recoveryCodesRemaining: number
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null

  // Actions
  initialize: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, fullName: string) => Promise<void>
  signOut: () => Promise<void>
  setUser: (user: User | null) => void
  updateDisplayName: (fullName: string) => Promise<void>
  setOrganization: (org: Organization | null) => void
  clearError: () => void
  refreshAal: () => Promise<void>
  setMfaStatus: (status: { enrolled: boolean; enrolledAt: string | null; recoveryCodesRemaining: number; required: boolean }) => void
}

async function fetchUserProfile(): Promise<UserProfile | null> {
  try {
    const { user } = await api.get<{ user: UserProfile }>('/users/me')
    return user
  } catch {
    return null
  }
}

async function fetchMfaStatus(): Promise<MfaStatusResponse | null> {
  try {
    return await api.get<MfaStatusResponse>('/users/me/mfa-status')
  } catch {
    return null
  }
}

// Shared by concurrent initialize() callers so one session restore serves all.
let initializePromise: Promise<void> | null = null

/**
 * The real session restore. Extracted from the store action so `initialize`
 * can be a thin de-duplicating wrapper around it.
 */
async function runInitialize(): Promise<void> {
  try {
    // Only show the global loading gate when there is no user yet. Flipping it
    // back to true right after sign-in unmounted the whole authenticated shell
    // and re-ran every mount effect underneath it (F8).
    if (useAuthStore.getState().isAuthenticated) {
      useAuthStore.setState({ error: null })
    } else {
      useAuthStore.setState({ isLoading: true, isRoleLoaded: false, error: null })
    }

    const session = await getSession()

    if (session?.user) {
      useAuthStore.setState({
        user: session.user,
        isAuthenticated: true,
        isLoading: false,
      })

      // Refresh AAL claim from Supabase JWT before any routing decision.
      await useAuthStore.getState().refreshAal()

      // Fetch user profile + MFA status. MFA status drives the
      // ProtectedRoute gating matrix; profile drives the rest.
      const [profile, mfaStatus] = await Promise.all([
        fetchUserProfile(),
        fetchMfaStatus(),
      ])
      if (!profile) useAuthStore.setState({ isRoleLoaded: true })
      if (profile) {
        // Treat absent fields (older backend without MFA migration) as
        // "not enrolled / not required" — using `!= null` would coerce
        // undefined to truthy and falsely trip ProtectedRoute's MFA gate.
        // The role and the flag land in ONE update: split, a render could
        // see "loaded" with the 'member' default still in place.
        useAuthStore.setState({
          role: profile.role,
          isRoleLoaded: true,
          isPlatformAdmin: profile.isPlatformAdmin,
          isOrgActive: profile.isOrgActive,
          mfaEnrolled: Boolean(profile.mfaEnrolledAt),
          mfaEnrolledAt: profile.mfaEnrolledAt ?? null,
          mfaRequired: profile.mfaRequired ?? false,
        })
        identifyUser(session.user.id, {
          email: session.user.email ?? '',
          name: profile.fullName,
          organizationId: profile.organizationId,
          role: profile.role,
          isPlatformAdmin: profile.isPlatformAdmin,
          mfaEnrolled: Boolean(profile.mfaEnrolledAt),
        })
      }
      if (mfaStatus) {
        useAuthStore.setState({
          mfaEnrolled: mfaStatus.enrolled,
          mfaEnrolledAt: mfaStatus.enrolledAt,
          mfaRequired: mfaStatus.required,
          recoveryCodesRemaining: mfaStatus.recoveryCodesRemaining,
        })
      }
    } else {
      useAuthStore.setState({
        user: null,
        isAuthenticated: false,
        role: 'member',
        isRoleLoaded: true,
        isPlatformAdmin: false,
        isOrgActive: true,
        aal: 'aal1',
        mfaEnrolled: false,
        mfaEnrolledAt: null,
        mfaRequired: false,
        recoveryCodesRemaining: 0,
        isLoading: false,
      })
    }
  } catch (error) {
    useAuthStore.setState({
      error: error instanceof Error ? error.message : 'Failed to initialize',
      isLoading: false,
      isRoleLoaded: true,
    })
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      organization: null,
      role: 'member',
      // True until a restore or sign-in starts: nothing renders behind the
      // initial `isLoading` gate before `initialize` flips it.
      isRoleLoaded: true,
      isPlatformAdmin: false,
      isOrgActive: true,
      aal: 'aal1',
      mfaEnrolled: false,
      mfaEnrolledAt: null,
      mfaRequired: false,
      recoveryCodesRemaining: 0,
      isLoading: true,
      isAuthenticated: false,
      error: null,

      // Initialize auth state from session
      initialize: async () => {
        // Concurrent callers (ProtectedRoute's mount effect and LoginPage's)
        // share one session restore instead of racing two (F8).
        if (initializePromise) return initializePromise
        const run = runInitialize().finally(() => {
          initializePromise = null
        })
        initializePromise = run
        return run
      },


      // Sign in with email/password
      signIn: async (email: string, password: string) => {
        try {
          set({ isLoading: true, isRoleLoaded: false, error: null })
          const { user } = await signInWithEmail(email, password)

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })

          // Refresh AAL before profile fetch so ProtectedRoute sees the
          // post-sign-in AAL state (still aal1 until TOTP verified).
          await useAuthStore.getState().refreshAal()

          const [profile, mfaStatus] = await Promise.all([
            fetchUserProfile(),
            fetchMfaStatus(),
          ])
          if (!profile) set({ isRoleLoaded: true })
          if (profile) {
            set({
              role: profile.role,
              isRoleLoaded: true,
              isPlatformAdmin: profile.isPlatformAdmin,
              isOrgActive: profile.isOrgActive,
              mfaEnrolled: Boolean(profile.mfaEnrolledAt),
              mfaEnrolledAt: profile.mfaEnrolledAt ?? null,
              mfaRequired: profile.mfaRequired ?? false,
            })
            identifyUser(user.id, {
              email: user.email ?? '',
              name: profile.fullName,
              organizationId: profile.organizationId,
              role: profile.role,
              isPlatformAdmin: profile.isPlatformAdmin,
              mfaEnrolled: Boolean(profile.mfaEnrolledAt),
            })
          }
          if (mfaStatus) {
            set({
              mfaEnrolled: mfaStatus.enrolled,
              mfaEnrolledAt: mfaStatus.enrolledAt,
              mfaRequired: mfaStatus.required,
              recoveryCodesRemaining: mfaStatus.recoveryCodesRemaining,
            })
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Sign in failed',
            isLoading: false,
            isRoleLoaded: true,
          })
          throw error
        }
      },

      // Sign up with email/password
      signUp: async (email: string, password: string, fullName: string) => {
        try {
          set({ isLoading: true, error: null })
          const { user } = await signUpWithEmail(email, password, fullName)

          set({
            user,
            isAuthenticated: !!user,
            isLoading: false,
          })

          captureEvent('user_signed_up')
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Sign up failed',
            isLoading: false,
          })
          throw error
        }
      },

      // Sign out
      signOut: async () => {
        try {
          set({ isLoading: true, error: null })
          await supabaseSignOut()

          resetUser()
          resetAllStores()

          set({
            user: null,
            organization: null,
            role: 'member',
            isPlatformAdmin: false,
            isOrgActive: true,
            aal: 'aal1',
            mfaEnrolled: false,
            mfaEnrolledAt: null,
            mfaRequired: false,
            recoveryCodesRemaining: 0,
            isAuthenticated: false,
            isLoading: false,
          })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Sign out failed',
            isLoading: false,
          })
          throw error
        }
      },

      // Set user (for auth state listener)
      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user,
        })
      },

      // Self-service display name. The name lives in two places that must not
      // drift: the `users` row (rendered on comments, @mentions, assignments
      // and Users & Access) and Supabase user metadata (the sidebar label and
      // avatar initials). The DB write goes first — it is the one that can be
      // rejected — so a failure leaves both sides on the old name.
      updateDisplayName: async (fullName: string) => {
        const trimmed = fullName.trim()
        if (!trimmed) {
          throw new Error('Name is required')
        }
        const current = useAuthStore.getState().user
        if (!current) {
          throw new Error('You must be signed in to change your name')
        }

        await updateOrganizationUserName(current.id, trimmed)
        const refreshed = await updateUserDisplayName(trimmed)
        set({ user: refreshed ?? current })
      },

      // Set organization
      setOrganization: (org: Organization | null) => {
        set({ organization: org })
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Refresh AAL claim from the current Supabase session. Called after
      // sign-in, on TOKEN_REFRESHED, and after MFA_CHALLENGE_VERIFIED.
      refreshAal: async () => {
        try {
          const data = await mfaGetAal()
          const level = data?.currentLevel === 'aal2' ? 'aal2' : 'aal1'
          set({ aal: level })
        } catch {
          // Non-fatal — leave existing AAL state in place. ProtectedRoute
          // falls back to fail-closed (treats missing AAL as aal1).
        }
      },

      // Update the MFA-status snapshot. Called after enroll, regenerate,
      // unenroll, or any other op that changes recovery-codes-remaining.
      setMfaStatus: (status) => {
        set({
          mfaEnrolled: status.enrolled,
          mfaEnrolledAt: status.enrolledAt,
          mfaRequired: status.required,
          recoveryCodesRemaining: status.recoveryCodesRemaining,
        })
      },
    }),
    {
      name: 'bidcontext-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist non-sensitive data — isPlatformAdmin + isOrgActive always re-fetched from server
        organization: state.organization,
      }),
    }
  )
)

// Set up auth state listener
supabase.auth.onAuthStateChange((event, session) => {
  const { setUser, refreshAal } = useAuthStore.getState()

  if (event === 'SIGNED_IN' && session?.user) {
    setUser(session.user)
    void refreshAal()
  } else if (event === 'SIGNED_OUT') {
    setUser(null)
    resetUser()
    resetAllStores()
  } else if (event === 'TOKEN_REFRESHED' && session?.user) {
    setUser(session.user)
    void refreshAal()
  } else if (event === 'MFA_CHALLENGE_VERIFIED' && session?.user) {
    setUser(session.user)
    void refreshAal()
    // UX hint for PlatformAdminRoute step-up freshness check.
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('bc.mfa.lastAal2At', String(Date.now()))
    }
  }
})
